import { observer } from 'mobx-react-lite'
import { useServiceStore } from '../ServiceStore'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import validationConstants from '@gap/utils/validation/validation-constants'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import FormControl from '@mui/material/FormControl'
import OutlinedInput from '@mui/material/OutlinedInput'
import Stack from '@mui/material/Stack'
import ServiceActionCancelOrDiscard from './actions/ServiceActionCancelOrDiscard'
import ServiceActionSave from './actions/ServiceActionSave'
import ServiceActionDelete from './actions/ServiceActionDelete'
import ServiceAutocomplete from './ServiceAutocomplete'
import AutocompleteSingle from 'reusable/mui/auto-complete/AutocompleteSingle'
import variables from 'variables/variables'
import { themeConfig } from '@gap/shared/theme'
import { FeatureEnum } from '@gap/backend/app-services/feature/models/FeatureEnum'
import Tooltip from '@mui/material/Tooltip'
import LexicalRichTextEditor from 'reusable/rich-text-editors/lexical/LexicalRichTextEditor'
import { defaultToolbarShowOptions } from 'reusable/rich-text-editors/lexical/plugins/ToolbarPlugin/constants'
import GapAlert from 'reusable/mui/alert/GapAlert'

export default observer(function ServiceItem(props: { source: FeatureEnum, startingValue?: string }) {
   const serviceStore = useServiceStore()
   const form = serviceStore.formHandler
   serviceStore.instantiatedSource = props.source

   return (
      <Drawer
         data-testid='service-item-drawer'
         anchor="right"
         open={serviceStore.serviceDrawer.visible}
         onClose={serviceStore.closeServiceDrawerButtonHandler}
         sx={{
            zIndex: themeConfig.dialog.zIndex + 1
         }}
      >
         <Box sx={{ width: '100%', maxWidth: '715px', px: 4, py: 2 }} role="presentation">
            <Grid sx={{ mb: 6 }} size={12}>
               <Grid container spacing={3}>
                  <Grid size={12}>
                     <Typography variant="h6">{serviceStore.drawerTitle}</Typography>
                  </Grid>

                  {serviceStore.linkedGapProcess && <Grid sx={{ mt: 3 }} size={12}>
                     <GapAlert severity="info" icon={serviceStore.linkedGapProcess.description ? undefined : false}>
                        This service adheres to the <Tooltip title={serviceStore.linkedGapProcess.description}>
                           <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                              {serviceStore.linkedGapProcess.name}
                           </span>
                        </Tooltip> process, so we recommend keeping changes to a minimum to avoid any unexpected behaviour. If you’d like to make any changes which may no longer reflect the original service, you may want to create a new service instead.
                     </GapAlert>
                  </Grid>}

                  <Grid size={12}>
                     {
                        form.name.bindInput((ip) =>
                           serviceStore.currentOrg?.orgType === 'advisor' && serviceStore.serviceAndCategoryList?.length && props.source !== 'proposals'
                              ? <ServiceAutocomplete
                                 data-testid='advisor-service-name'
                                 services={serviceStore.services.filter(({ orgId }) => orgId === variables.gapOrgId)}
                                 autoFocus
                                 onSelectPreconfiguredService={serviceStore.preconfiguredServiceField}
                                 label={serviceStore.serviceNameLabel}
                                 {...ip}
                              />
                              : <TextField
                                 data-testid='gap-service-name'
                                 label={serviceStore.serviceNameLabel}
                                 autoFocus
                                 fullWidth
                                 variant='outlined'
                                 {...ip}
                              />
                        )
                     }
                  </Grid>

                  <Grid size={12}>
                     {form.description.bindInput(ip => <TextField data-testid='service-definition' fullWidth label="Definition" multiline minRows={'2'} slotProps={{ htmlInput: { maxLength: validationConstants.maxServiceDefintionLength } }} {...ip} />)}
                  </Grid>
                  <Grid size={12}>
                     {form.howItWorks.bindInput(ip => <LexicalRichTextEditor testid='service-how-it-works' label='How It Works' key={`service-how-it-works-${serviceStore.howItWorksKeySuffix}`} hideResize={true} {...ip} toolbarShowOptions={defaultToolbarShowOptions} />)}
                  </Grid>
                  <Grid size={12}>
                     <ServiceCategory />
                  </Grid>

                  {/** ADVISOR ONLY FIELDS */}
                  {
                     serviceStore.currentOrg?.orgType === 'advisor' && <>
                        <Grid size={12}>
                           <Typography variant="subtitle2">Starting price</Typography>
                        </Grid>
                        <Grid sx={{ display: 'flex', gap: 1 }} size={12}>
                           {form.startingPrice.bindInput(({ helperText, ...ip }) =>
                              <FormControl sx={{ width: '115px' }}>
                                 <OutlinedInput
                                    startAdornment={<InputAdornment position="start">{serviceStore.currencySymbolForSelectedService}</InputAdornment>}
                                    {...ip}
                                 />
                              </FormControl>
                           )}
                           <ServiceStartingPriceRate />
                        </Grid>
                     </>
                  }
               </Grid>
            </Grid>
            <Stack direction='row' justifyContent='space-between'>
               <Box>
                  <Stack direction='row' spacing={1}>
                     <ServiceActionSave />
                     <ServiceActionCancelOrDiscard />
                  </Stack>
               </Box>
               {serviceStore.selectedServiceId &&
                  <ServiceActionDelete />
               }
            </Stack>
         </Box>
      </Drawer>
   );
})

const ServiceCategory = observer(function ServiceCategory() {
   const serviceStore = useServiceStore()
   const form = serviceStore.formHandler

   return <>
      {form.category.bindInput(ip => <AutocompleteSingle data-testid='service-category' disablePortal options={serviceStore.serviceCategoryOptions} label="Category" {...ip} />)}
   </>
})

const ServiceStartingPriceRate = observer(function ServiceStartingPriceRate() {
   const serviceStore = useServiceStore()
   const form = serviceStore.formHandler

   return <>
      {form.startingPriceRate.bindInput(ip => <AutocompleteSingle disablePortal options={serviceStore.serviceRateOptions} label="Rate" sx={{ flexGrow: '1' }} {...ip} />)}
   </>
})